import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// Placeholder images for bathroom aesthetics
const bathroomImages = {
  hero: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80",
  pvc: "https://images.unsplash.com/photo-1620626011761-9963d7521576?auto=format&fit=crop&w=600&q=80",
  privacy: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=600&q=80",
};

function BathroomBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-gray-800">
      <Helmet>
        <title>Bathroom Blinds UK | 100% Waterproof & Moisture Resistant</title>
        <meta
          name="description"
          content="Upgrade your space with 100% waterproof bathroom blinds. Shop made-to-measure PVC rollers, faux wood venetians, and anti-mould window treatments at MF Blinds."
        />
        <meta name="keywords" content="Bathroom Blinds UK, Waterproof Blinds, PVC Roller Blinds, Faux Wood Venetians, Moisture Resistant, Bathroom Privacy Blinds" />
      </Helmet>

      {/* --- Section 1: Hero & SEO Hook --- */}
      <section className="grid lg:grid-cols-2 gap-12 items-center mb-16">
        <div>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight text-teal-900">
            Waterproof <br />
            <span className="text-teal-600">Bathroom Blinds</span>
          </h1>
          <p className="text-lg leading-relaxed mb-6">
            The bathroom is the most demanding room in the house. Between steam, humidity, and direct splashes, standard window coverings simply won't last. 
          </p>
          <p className="text-lg leading-relaxed mb-8">
            Our <strong>made-to-measure bathroom blinds</strong> are crafted from high-grade PVC and moisture-resistant polymers. They are designed to stay <strong>mould-free</strong> and easy to wipe clean, ensuring your sanctuary stays hygienic and stylish.
          </p>
          <div className="flex gap-4">
            <button className="bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition">Shop Waterproof Range</button>
            <Link to="/contact" className="border-2 border-teal-600 text-teal-600 px-8 py-3 rounded-lg font-bold hover:bg-teal-50 transition">Get a Quote</Link>
          </div>
        </div>
        <div className="relative">
          <img 
            src={bathroomImages.hero} 
            alt="Modern spa-style bathroom with white moisture resistant blinds" 
            className="rounded-2xl shadow-2xl object-cover h-[450px] w-full"
          />
        </div>
      </section>

      {/* --- Section 2: Product Breakdown (Semantic Hierarchy) --- */}
      <section className="py-12 border-t border-gray-100">
        <h2 className="text-3xl font-bold mb-10 text-center">Top Rated Blinds for Humid Spaces</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          
          {/* PVC Roller Blinds */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold mb-3">PVC Roller Blinds</h3>
            <p className="text-gray-600 mb-4 text-sm">100% waterproof and blackout. The ultimate choice for total privacy and zero-maintenance cleaning.</p>
            <Link to="/roller-blinds" className="text-teal-600 font-semibold text-sm">View PVC Rollers →</Link>
          </div>

          {/* Faux Wood Venetians */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold mb-3">Faux Wood Venetians</h3>
            <p className="text-gray-600 mb-4 text-sm">Get the look of real wood without the warping. These PVC slats handle steam effortlessly.</p>
            <Link to="/wooden-blinds" className="text-teal-600 font-semibold text-sm">View Faux Wood →</Link>
          </div>

          {/* Perfect Fit Blinds */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold mb-3">Perfect Fit Blinds</h3>
            <p className="text-gray-600 mb-4 text-sm">Clips directly to uPVC frames. Great for bathroom windows that need to be opened frequently for ventilation.</p>
            <Link to="/perfect-fit-blinds" className="text-teal-600 font-semibold text-sm">View Perfect Fit →</Link>
          </div>

        </div>
      </section>

      {/* --- Section 3: Viral Features / Trust Factors --- */}
      <section className="my-20 bg-teal-900 text-white rounded-3xl p-10 md:p-16 flex flex-col md:flex-row gap-10 items-center">
        <div className="md:w-1/2">
          <h2 className="text-3xl font-bold mb-6">Built for Modern Living</h2>
          <ul className="space-y-4 text-teal-100">
            <li className="flex items-center gap-3">
              <span className="text-2xl">🦠</span> <strong>Anti-Microbial:</strong> Treated to prevent fungal growth in damp conditions.
            </li>
            <li className="flex items-center gap-3">
              <span className="text-2xl">🔒</span> <strong>Total Privacy:</strong> High-opacity fabrics to ensure your bathroom is a private retreat.
            </li>
            <li className="flex items-center gap-3">
              <span className="text-2xl">🧽</span> <strong>Easy-Wipe:</strong> Dirt and condensation wipe off with a single stroke.
            </li>
          </ul>
        </div>
        <div className="md:w-1/2">
           <img src={bathroomImages.pvc} alt="Moisture resistant material detail" className="rounded-xl shadow-lg border-4 border-teal-800" />
        </div>
      </section>

      {/* --- Section 4: SEO FAQ --- */}
      <section className="mb-20">
        <h2 className="text-3xl font-bold mb-8">Expert Tips: Choosing Bathroom Blinds</h2>
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <h4 className="font-bold text-lg mb-2">Can I use fabric Roman blinds in a bathroom?</h4>
            <p className="text-gray-600 italic mb-4">"It's risky."</p>
            <p className="text-gray-700">Unless the bathroom is exceptionally large and well-ventilated, traditional fabrics can absorb moisture and develop mould. We recommend our **PVC Roller** range instead.</p>
          </div>
          <div>
            <h4 className="font-bold text-lg mb-2">Which blinds are best for privacy?</h4>
            <p className="text-gray-600 italic mb-4">"Control is key."</p>
            <p className="text-gray-700">Venetian blinds (Faux Wood) are excellent because you can tilt the slats to let in light while blocking the view from outside.</p>
          </div>
        </div>
      </section>

      {/* --- Section 5: Internal Link Navigation --- */}
      <div className="border-t pt-12 text-center">
        <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-6">Explore Other Rooms</p>
        <div className="flex flex-wrap justify-center gap-6">
          <Link to="/kitchen-blinds" className="hover:text-teal-600 font-medium">Kitchen Blinds</Link>
          <Link to="/bedroom-blinds" className="hover:text-teal-600 font-medium">Bedroom Blinds</Link>
          <Link to="/living-room-blinds" className="hover:text-teal-600 font-medium">Living Room Blinds</Link>
          <Link to="/conservatory-blinds" className="hover:text-teal-600 font-medium">Conservatory Blinds</Link>
        </div>
      </div>

      {/* --- Final CTA --- */}
      <footer className="mt-20 text-center">
        <div className="bg-gray-100 py-12 px-6 rounded-2xl">
          <h2 className="text-3xl font-bold mb-4">Find Your Perfect Match</h2>
          <p className="mb-8 max-w-2xl mx-auto">Get free samples delivered to your door and see the quality of our moisture-resistant fabrics for yourself.</p>
          <button className="bg-teal-600 text-white px-10 py-4 rounded-full text-lg font-bold shadow-lg hover:bg-teal-700 transition">
             Order Free Samples
          </button>
        </div>
      </footer>
    </div>
  );
}

export default BathroomBlinds;